/* eslint-disable @typescript-eslint/no-explicit-any */
import { useWebSocketContext } from '@/context/websocket-context';
import { useAppDispatch } from '@/store';
import { extensionApi } from '@/store/features/extension.feature';
import { useCallback, useEffect, useRef } from 'react';
import {
  isCreditUpdateMessage,
  isEnrichmentMessage,
  isSmartColumnMessage,
  isSmartColumnLeadUpdateMessage,
  isActionColumnLeadUpdateMessage,
  isActionColumnProgressMessage,
  isBulkProspectingMessage,
  isCompanyEnrichmentProgressMessage,
  isFindPeopleProgressMessage,
  isPersonalEmailProgressMessage,
  type CreditUpdateMessage,
} from './use-websocket.hook';

/** How often the balance is re-read while running Clu work holds credits. */
const HELD_REFRESH_MS = 15_000;

/**
 * Hook that keeps the credit count in sync by:
 * 1. Listening for `credit_update` WebSocket messages and updating the RTK Query cache directly
 * 2. Listening for credit-consuming events (enrichment, smart columns, action
 *    columns, and personal email / company enrichment / find people on the
 *    leads channel) and invalidating the credits cache so it refetches
 * 3. Re-reading the balance while running Clu work holds credits
 *
 * Mount this in a component that is always rendered (e.g., Navbar).
 */
export const useCreditSync = (organizationId: string) => {
  const dispatch = useAppDispatch();
  // What running Clu work holds is the org's: a teammate's chat, a Slack turn
  // or an agent run settles without anything telling this tab. While a hold
  // shows, re-read until it settles back.
  const { held } = extensionApi.endpoints.getOrganizationCredits.useQueryState(
    { organizationId },
    {
      skip: !organizationId,
      selectFromResult: ({ currentData }) => ({
        held: (currentData?.reserved ?? 0) > 0,
      }),
    }
  );
  extensionApi.endpoints.getOrganizationCredits.useQuerySubscription(
    { organizationId },
    {
      skip: !organizationId || !held,
      pollingInterval: HELD_REFRESH_MS,
      skipPollingIfUnfocused: true,
    }
  );
  const { subscribe } = useWebSocketContext();
  const activeOrganizationIdRef = useRef(organizationId);
  activeOrganizationIdRef.current = organizationId;

  // Debounce invalidation to avoid rapid-fire refetches during bulk operations
  const invalidateTimerRef = useRef<NodeJS.Timeout | null>(null);

  const scheduleInvalidation = useCallback(() => {
    if (!organizationId) return;
    if (invalidateTimerRef.current) {
      clearTimeout(invalidateTimerRef.current);
    }
    invalidateTimerRef.current = setTimeout(() => {
      if (activeOrganizationIdRef.current === organizationId) {
        dispatch(
          extensionApi.util.invalidateTags([
            { type: 'Credits', id: organizationId },
          ])
        );
      }
      invalidateTimerRef.current = null;
    }, 1000);
  }, [dispatch, organizationId]);

  useEffect(() => {
    if (!organizationId) return;

    const unsubscribe = subscribe('credits', (msg) => {
      if (isCreditUpdateMessage(msg)) {
        const creditMsg = msg as CreditUpdateMessage;
        if (
          activeOrganizationIdRef.current !== organizationId ||
          // The socket carries every org of the user (routing is per user),
          // and the backend stamps every balance, so an unstamped or
          // mismatched one can't be trusted to be this org's.
          creditMsg.organization_id !== organizationId
        ) {
          return;
        }
        // Update the RTK Query cache directly with the new balance
        dispatch(
          extensionApi.util.updateQueryData(
            'getOrganizationCredits',
            { organizationId },
            (draft) => {
              draft.count = creditMsg.count;
              // The count is net of what running Clu work holds, so the
              // held line moves with it. A balance sent without the held
              // amount keeps the last one read.
              if (typeof creditMsg.reserved === 'number') {
                draft.reserved = creditMsg.reserved;
              }
              draft.email_credits = creditMsg.email_credits;
              draft.phone_credits = creditMsg.phone_credits;
              draft.last_updated = creditMsg.last_updated;
            }
          )
        );
      }
    });

    return unsubscribe;
  }, [subscribe, dispatch, organizationId]);

  useEffect(() => {
    const unsubEnrichment = subscribe('enrichment', (msg) => {
      if (isEnrichmentMessage(msg)) {
        // Invalidate on completion or progress milestones
        const status = (msg as any).status;
        if (
          status === 'completed' ||
          status === 'session_completed' ||
          status === 'failed' ||
          status === 'progress' ||
          status === 'prospecting_completed'
        ) {
          scheduleInvalidation();
        }
      } else if (isBulkProspectingMessage(msg)) {
        scheduleInvalidation();
      }
    });

    const unsubSmartColumns = subscribe('smartColumns', (msg) => {
      if (isSmartColumnMessage(msg)) {
        const status = (msg as any).status;
        if (status === 'completed' || status === 'failed') {
          scheduleInvalidation();
        }
      } else if (isSmartColumnLeadUpdateMessage(msg)) {
        // Individual lead updates consume credits — debounced invalidation
        scheduleInvalidation();
      }
    });

    const unsubActionColumns = subscribe('actionColumns', (msg) => {
      if (
        isActionColumnLeadUpdateMessage(msg) ||
        isActionColumnProgressMessage(msg)
      ) {
        scheduleInvalidation();
      }
    });

    // These jobs report on the leads channel, and their charges push no
    // credit_update.
    const unsubLeads = subscribe('leads', (msg) => {
      if (
        isPersonalEmailProgressMessage(msg) ||
        isCompanyEnrichmentProgressMessage(msg) ||
        isFindPeopleProgressMessage(msg)
      ) {
        scheduleInvalidation();
      }
    });

    return () => {
      unsubEnrichment();
      unsubSmartColumns();
      unsubActionColumns();
      unsubLeads();
      if (invalidateTimerRef.current) {
        clearTimeout(invalidateTimerRef.current);
      }
    };
  }, [subscribe, scheduleInvalidation]);
};
